// Mirror of server/utils/dealModel.js modelKey() — must stay in sync.
// Extract model key from product description (e.g., "Emberton III Bluetooth Black" → "Emberton III").
export function modelKey(description) {
  if (!description) return '';
  const tokens = description.trim().split(/\s+/);
  if (tokens.length === 0) return '';
  const second = (tokens[1] || '').toUpperCase();
  const isRoman = /^(?:I{1,3}|IV|V|VI|VII|VIII|IX|X)$/.test(second);
  const isDigit = /^\d+$/.test(second);
  if (isRoman || isDigit) return `${tokens[0]} ${tokens[1]}`;
  return tokens[0];
}

// Extract color/variant from description by stripping model + common middle/suffix tokens.
// "Emberton III Bluetooth Black"        → "Black"
// "Willen II Black and Brass"           → "Black and Brass"
// "Motif II A.N.C. Black"               → "Black"
// "Stanmore III Bluetooth Brown EU"     → "Brown"
// "Acton III Bluetooth Burgundy (EU)"   → "Burgundy"
// "HESTON 120 (EU)"                     → "—" (no color)
export function colorKey(description) {
  if (!description) return '—';
  const m = modelKey(description);
  let rest = description.trim();
  if (m && rest.toLowerCase().startsWith(m.toLowerCase())) rest = rest.slice(m.length).trim();
  rest = rest.replace(/\b(Bluetooth|BT|ANC|A\.N\.C\.|Gift\s*Box|Sub)\b/gi, ' ');
  rest = rest
    .replace(/\s*\((?:EU|US|EU\/US)\)\s*$/i, '')
    .replace(/\s+(?:CN|EU|US)(?:\s*\(EU\))?\s*$/i, '')
    .replace(/\bCN\b/gi, ' ')
    .replace(/-+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return rest || '—';
}

// Group items by modelKey + colorKey.
// Returns: [{ model, totalQty, totalValue, lineCount, colors: [{ color, qty, value, lineCount }] }]
// qtyField: 'effectiveQty' (uses customerQty when set, else originalQty) | 'originalQty'
export function aggregateByModel(items, qtyField = 'effectiveQty') {
  const map = new Map();
  for (const it of items || []) {
    const m = modelKey(it.description) || it.sku || '?';
    const c = colorKey(it.description);
    const qty = qtyField === 'effectiveQty'
      ? (it.customerQty != null ? Number(it.customerQty) : Number(it.originalQty))
      : Number(it.originalQty);
    if (!Number.isFinite(qty) || qty === 0) continue;
    const price = Number(it.customerPrice) || 0;
    const value = qty * price;

    if (!map.has(m)) map.set(m, { model: m, totalQty: 0, totalValue: 0, lineCount: 0, colors: new Map() });
    const entry = map.get(m);
    entry.totalQty += qty;
    entry.totalValue += value;
    entry.lineCount += 1;

    const col = entry.colors.get(c) || { color: c, qty: 0, value: 0, lineCount: 0 };
    col.qty += qty;
    col.value += value;
    col.lineCount += 1;
    entry.colors.set(c, col);
  }
  return [...map.values()]
    .map(m => ({ ...m, colors: [...m.colors.values()].sort((a, b) => b.qty - a.qty) }))
    .sort((a, b) => b.totalQty - a.totalQty);
}
